/* ----------------------------------------------------------------------------
   GIFT WRAP — custom parcel-based gift wrapping
   ----------------------------------------------------------------------------
   Data model (single source of truth = the cart itself):
   * Item lines:   "Gift wrap: Parcel N (Style)" | "Gift note: Parcel N"  (visible)
                   "_gw_parcel: N"                                        (hidden)
   * Fee lines:    one gift-wrap-product line per wrapped parcel with
                   "Gift wrap: Parcel N (Style)", "_gw_fee: N", "_gw_style: Style"
   * Messages:     cart attributes "Gift note (Parcel N)" + fee-line "Gift note"
   *                property + a structured block appended to the order note
   *                (picklists print note + line properties, not attributes)

   The dialog is server-rendered by gift-wrap-dialog.liquid from live cart
   state; this module edits it client-side and applies everything on Save.
   Coexists with app-written line properties (BCPO etc.) by always merging
   full property maps instead of replacing them.
   ---------------------------------------------------------------------------- */

const GW_HIDDEN_KEY = '_gw_parcel';
const GW_WRAP_LABEL = 'Gift wrap';
const GW_NOTE_LABEL = 'Gift note';
const GW_FEE_KEY = '_gw_fee';
const GW_STYLE_KEY = '_gw_style';
const GW_ATTR_RE = /^Gift note \(Parcel (.+)\)$/;

/* Translated UI strings (injected by snippets/clinks-i18n.liquid); inline
   English is the fallback. Order-data strings — line properties, cart
   attributes, the order-note block — stay English for the packing team. */
const GW_I18N = (window.clinksI18n && window.clinksI18n.gw) || {};
const gwT = (key, fallback) => GW_I18N[key] || fallback;

const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

/* ------------------------------ cart helpers ------------------------------ */

async function cartRequest(path, payload) {
  const response = await fetch(`${root()}cart/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let description = `Cart update failed (${response.status})`;
    try {
      const body = await response.json();
      description = body.description || body.message || description;
    } catch (e) { /* keep default */ }
    const error = new Error(description);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

const fetchCart = async () => (await fetch(`${root()}cart.js`)).json();
const changeLine = (id, quantity, properties) =>
  cartRequest('change.js', properties === undefined ? { id, quantity } : { id, quantity, properties });
const addLine = (id, quantity, properties, sellingPlan) =>
  cartRequest('add.js', { items: [{ id, quantity, properties, ...(sellingPlan ? { selling_plan: sellingPlan } : {}) }] });
const updateAttributes = (attributes) => cartRequest('update.js', { attributes });

/* ----------------------------- props helpers ------------------------------ */

function stripGw(properties) {
  const clean = { ...(properties || {}) };
  delete clean[GW_HIDDEN_KEY];
  delete clean[GW_WRAP_LABEL];
  delete clean[GW_NOTE_LABEL];
  return clean;
}

function hasGw(properties) {
  const props = properties || {};
  return GW_HIDDEN_KEY in props || GW_WRAP_LABEL in props || GW_NOTE_LABEL in props;
}

function propsEqual(a, b) {
  const na = a || {}, nb = b || {};
  const ka = Object.keys(na).sort(), kb = Object.keys(nb).sort();
  if (ka.length !== kb.length) return false;
  return ka.every((k, i) => k === kb[i] && String(na[k]) === String(nb[k]));
}

function giftPropsFor(baseProps, pid, parcel) {
  const props = { ...baseProps, [GW_HIDDEN_KEY]: String(pid) };
  if (parcel.wrap) {
    props[GW_WRAP_LABEL] = `Parcel ${pid} (${parcel.style})`;
    // wrapped AND carrying a note -> both labels, so pickers see the note flag
    if ((parcel.message || '').trim()) props[GW_NOTE_LABEL] = `Parcel ${pid}`;
  } else {
    props[GW_NOTE_LABEL] = `Parcel ${pid}`;
  }
  return props;
}

function feePropsFor(pid, style, message) {
  const props = {
    [GW_WRAP_LABEL]: `Parcel ${pid} (${style})`,
    [GW_FEE_KEY]: String(pid),
    [GW_STYLE_KEY]: style
  };
  // the note text rides on the fee line so it prints with line properties
  // (picklists don't render cart attributes)
  if ((message || '').trim()) props[GW_NOTE_LABEL] = message.trim();
  return props;
}

function formatMoney(cents) {
  // match the theme's money_without_trailing_zeros: $5, not $5.00
  const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
  const format = window.themeVariables?.settings?.moneyFormat;
  if (typeof format === 'string' && format.includes('{{')) {
    return format.replace(/{{\s*amount[a-z_]*\s*}}/i, amount).replace(/<[^>]+>/g, '');
  }
  return `$${amount}`;
}

const loading = (on) =>
  document.documentElement.dispatchEvent(new CustomEvent(on ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true }));

const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

function refreshCartUI() {
  if (isCartPage()) {
    window.location.reload();
  } else {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
}

/* --------------------------- apply desired state -------------------------- */
/*
  desired = {
    items:   [{ key, variantId, allocations: [{ parcelId | '', qty }] }],
    parcels: { pid: { wrap, style, message } }   // only parcels that have members
  }
  A line's units can be spread over several parcels (allocations) — the norm
  for gifting: 5 cufflinks + 5 tie bars wrapped as 5 matching-set parcels.

  Execution is tracked against the LIVE cart: every change/add response IS the
  updated cart. Line keys are unstable — apps and cart transforms can split one
  quantity into several same-variant lines whose keys regenerate on writes
  (observed 22 Sep 2026: a transform-split qty-2 line 400'd the chain mid-save,
  leaving members tagged but fees uncharged — the #408433 failure class). So
  same-variant lines with identical properties are treated as ONE logical line,
  and every write resolves its target from the live cart at that moment.
*/
async function applyGiftState(desired, feeVariantId, styleVariants) {
  let liveCart = await fetchCart();
  const isFee = (item) => GW_FEE_KEY in (item.properties || {});
  const track = (result) => {
    // full carts carry a token; add.js responses ({items: added lines}) do not
    if (result && Array.isArray(result.items) && result.token) liveCart = result;
    return result;
  };

  // ---- group same-variant lines with identical properties into logical lines
  const groups = [];
  const groupByKey = new Map();
  for (const item of liveCart.items) {
    if (isFee(item)) continue;
    const variantId = item.variant_id || item.id;
    let group = groups.find((candidate) => candidate.variantId === variantId && propsEqual(candidate.properties, item.properties));
    if (!group) {
      group = {
        variantId,
        properties: item.properties || {},
        quantity: 0,
        keys: [],
        sellingPlan: item.selling_plan_allocation?.selling_plan?.id
      };
      groups.push(group);
    }
    group.quantity += item.quantity;
    group.keys.push(item.key);
    groupByKey.set(item.key, group);
  }

  // ---- fold the dialog's rows into those logical lines
  const entries = new Map();
  for (const row of desired.items) {
    const group = groupByKey.get(row.key);
    if (!group) continue; // line disappeared since render
    const entry = entries.get(group) || { group, allocations: [] };
    entry.allocations.push(...(row.allocations || []));
    entries.set(group, entry);
  }

  // ---- plan: coalesce each logical line's allocations into property chunks
  const rowOps = [];
  for (const { group, allocations } of entries.values()) {
    const base = stripGw(group.properties);
    const chunks = [];
    for (const alloc of allocations) {
      if (!alloc.qty || alloc.qty <= 0) continue;
      const parcel = (alloc.parcelId && desired.parcels[alloc.parcelId]) || null;
      const props = parcel ? giftPropsFor(base, alloc.parcelId, parcel) : base;
      const twin = chunks.find((chunk) => propsEqual(chunk.props, props));
      if (twin) twin.qty += alloc.qty;
      else chunks.push({ props, qty: alloc.qty });
    }
    if (chunks.length === 0) chunks.push({ props: base, qty: group.quantity });

    // The dialog snapshot can be stale — clamp to what the cart holds NOW;
    // surplus units follow the first chunk.
    let remaining = group.quantity;
    for (const chunk of chunks) {
      chunk.qty = Math.min(chunk.qty, remaining);
      remaining -= chunk.qty;
    }
    chunks[0].qty += remaining;
    const liveChunks = chunks.filter((chunk) => chunk.qty > 0);

    // one line, one chunk, nothing changes -> skip
    if (liveChunks.length === 1 && group.keys.length === 1 && propsEqual(group.properties, liveChunks[0].props)) continue;
    rowOps.push({ group, chunks: liveChunks });
  }

  try {
    // ---- execute rows.
    // change.js and update.js respond with the FULL cart (tracked); add.js
    // responds with only the added lines — never track it, refetch instead.
    for (const op of rowOps) {
      const findSource = () =>
        liveCart.items.find(
          (item) =>
            !isFee(item) &&
            (item.variant_id || item.id) === op.group.variantId &&
            (op.group.keys.includes(item.key) || propsEqual(item.properties, op.group.properties))
        );

      if (op.group.keys.length > 1) {
        // Transform/app-split views of ONE underlying line: property writes
        // propagate across every view (observed 22 Sep 2026), so per-view
        // rewrites can't split them. Rewrite wholesale: zero the group, then
        // add each chunk back with exact quantity and properties.
        let source;
        while ((source = findSource())) {
          try {
            track(await changeLine(source.key, 0));
          } catch (error) { /* view already retired server-side */ }
          liveCart = await fetchCart(); // zeroing one view can retire siblings
        }
        let added = 0;
        try {
          for (const chunk of op.chunks) {
            await addLine(op.group.variantId, chunk.qty, Object.keys(chunk.props).length > 0 ? chunk.props : undefined, op.group.sellingPlan);
            added += chunk.qty;
          }
        } catch (error) {
          const missing = op.chunks.reduce((sum, chunk) => sum + chunk.qty, 0) - added;
          if (missing > 0) await addLine(op.group.variantId, missing, op.group.properties, op.group.sellingPlan).catch(() => {});
          throw error;
        } finally {
          liveCart = await fetchCart();
        }
      } else {
        // Normal single line: first chunk rewrites it, the rest are re-added
        // (add.js merges identical props into existing lines on its own).
        const source = findSource();
        const [first, ...rest] = op.chunks;
        if (source && Object.keys(first.props).length > 0) {
          track(await changeLine(source.key, first.qty, first.props));
        } else if (source) {
          // change.js silently ignores properties: {} — clearing the last
          // properties from a line needs remove + re-add
          track(await changeLine(source.key, 0));
          await addLine(op.group.variantId, first.qty, undefined, op.group.sellingPlan);
          liveCart = await fetchCart();
        } else {
          await addLine(op.group.variantId, first.qty, first.props, op.group.sellingPlan);
        }
        let placed = first.qty;
        try {
          for (const chunk of rest) {
            await addLine(op.group.variantId, chunk.qty, Object.keys(chunk.props).length > 0 ? chunk.props : undefined, op.group.sellingPlan);
            placed += chunk.qty;
          }
        } catch (error) {
          const missing = op.chunks.reduce((sum, chunk) => sum + chunk.qty, 0) - placed;
          if (missing > 0) await addLine(op.group.variantId, missing, op.group.properties, op.group.sellingPlan).catch(() => {});
          throw error;
        } finally {
          if (!source || rest.length > 0) liveCart = await fetchCart();
        }
      }
    }

    // ---- fees: one line per wrapped parcel, judged from the LIVE cart so a
    // parcel that lost its members mid-flight never gets charged
    const memberPids = new Set();
    for (const item of liveCart.items) {
      if (isFee(item)) continue;
      const pid = (item.properties || {})[GW_HIDDEN_KEY];
      if (pid) memberPids.add(String(pid));
    }
    const wrappedPids = [...memberPids].filter((pid) => desired.parcels[pid] && desired.parcels[pid].wrap);
    const feeVariantFor = (style) => (styleVariants && styleVariants[style]) || feeVariantId;
    const feeLineFor = (pid) => liveCart.items.find((item) => isFee(item) && String(item.properties[GW_FEE_KEY]) === pid);

    for (const feeLine of liveCart.items.filter(isFee)) {
      const pid = String(feeLine.properties[GW_FEE_KEY]);
      if (wrappedPids.includes(pid)) continue;
      const live = feeLineFor(pid);
      if (live) track(await changeLine(live.key, 0));
    }
    for (const pid of wrappedPids) {
      const parcel = desired.parcels[pid];
      const wantedVariant = feeVariantFor(parcel.style);
      const wanted = feePropsFor(pid, parcel.style, parcel.message);
      const live = feeLineFor(pid);
      if (!live) {
        await addLine(wantedVariant, 1, wanted);
      } else if ((live.variant_id || live.id) !== wantedVariant) {
        // each style is its own variant (so the cart shows the right paper) —
        // a style change means swapping the fee line, not just its props
        track(await changeLine(live.key, 0));
        await addLine(wantedVariant, 1, wanted);
      } else if (live.quantity !== 1 || !propsEqual(live.properties, wanted)) {
        track(await changeLine(live.key, 1, wanted));
      }
    }

    // ---- message attributes (null removes), for parcels that really exist
    const attributes = {};
    for (const key of Object.keys(liveCart.attributes || {})) {
      if (GW_ATTR_RE.test(key)) attributes[key] = null;
    }
    for (const pid of memberPids) {
      const message = (desired.parcels[pid]?.message || '').trim();
      if (message) attributes[`Gift note (Parcel ${pid})`] = message;
    }
    if (Object.keys(attributes).length > 0) await updateAttributes(attributes);
  } finally {
    // Whatever happened above — success, partial failure, 400 mid-chain —
    // the order-note block must describe the cart as it actually is.
    await syncOrderNoteRetry();
  }
}

/* ----------------------------- order-note block ----------------------------
   Picklist programs print the order NOTE and line properties, but not cart
   attributes — so a structured summary of every parcel (style, members, note)
   is appended to the note, after whatever the customer typed. */
const GW_NOTE_DELIM = '\u2014 GIFT WRAPPING \u2014';

/* Latest known block text, cached so it can be restored synchronously into the
   note textarea whenever its value is about to be transmitted (see guardOrderNote). */
let gwNoteBlockCache = '';

function gwWithBlock(customer) {
  if (!gwNoteBlockCache || customer.indexOf(GW_NOTE_DELIM) !== -1) return customer;
  return (customer ? customer + '\n\n' : '') + gwNoteBlockCache;
}

function buildNoteBlock(cart) {
  const members = {}, styles = {}, notes = {};
  for (const item of cart.items) {
    const props = item.properties || {};
    if (GW_FEE_KEY in props) {
      styles[String(props[GW_FEE_KEY])] = props[GW_STYLE_KEY] || '';
      continue;
    }
    const pid = props[GW_HIDDEN_KEY];
    if (pid) (members[pid] = members[pid] || []).push(item.quantity > 1 ? item.product_title + ' \u00d7' + item.quantity : item.product_title);
  }
  for (const [key, value] of Object.entries(cart.attributes || {})) {
    const match = GW_ATTR_RE.exec(key);
    if (match && value) notes[String(match[1])] = String(value);
  }
  const pids = [...new Set([...Object.keys(members), ...Object.keys(styles), ...Object.keys(notes)])].sort((a, b) => Number(a) - Number(b));
  if (pids.length === 0) return '';
  const lines = [GW_NOTE_DELIM];
  for (const pid of pids) {
    lines.push('Parcel ' + pid + ' \u2014 ' + (styles[pid] ? styles[pid] + ' wrap' : 'no wrap') + ' \u2014 ' + (members[pid] || []).join('; '));
    if (notes[pid]) lines.push('Note: ' + notes[pid].replace(/\s*\n\s*/g, ' / '));
  }
  return lines.join('\n');
}

async function syncOrderNote(cartMaybe) {
  const cart = cartMaybe || (await fetchCart());
  const note = String(cart.note || '');
  const delimIndex = note.indexOf(GW_NOTE_DELIM);
  const customerPart = (delimIndex === -1 ? note : note.slice(0, delimIndex)).replace(/\s+$/, '');
  const block = buildNoteBlock(cart);
  gwNoteBlockCache = block;
  const next = block ? (customerPart ? customerPart + '\n\n' : '') + block : customerPart;
  if (next !== note) await cartRequest('update.js', { note: next });
}

/* The note block is the packing list's source of truth and its write is often
   the LAST request of a long chain — mobile networks and the storefront
   bot-protection can drop it (attributes saved, note missing: order #406836).
   Retry, and never fail silently. */
async function syncOrderNoteRetry() {
  let noteError = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await syncOrderNote();
      return;
    } catch (error) {
      noteError = error;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  console.warn('[gift-wrap] order-note sync failed after retries:', noteError);
}

/* The theme's own cart-note textarea posts its raw value, which would wipe the
   appended block \u2014 hide the block from the textarea for DISPLAY, but restore it
   whenever the value is about to be TRANSMITTED. Two transmission paths:
   1. CartNote (theme.js) POSTs the value on 'change' \u2014 it reads it in the bubble
      phase, so restoring in the capture phase makes it post the full note.
   2. Tapping Checkout natively submits the cart form, which serializes the
      textarea \u2014 without the restore, the server overwrites the note with the
      stripped value at the moment of checkout and the packing list loses the
      gift messages (the "attributes survive, note empty" bug). */
(function guardOrderNote() {
  const strip = (textarea) => {
    const i = textarea.value.indexOf(GW_NOTE_DELIM);
    if (i !== -1) {
      gwNoteBlockCache = textarea.value.slice(i);
      textarea.value = textarea.value.slice(0, i).replace(/\s+$/, '');
    }
  };
  const stripAll = () => document.querySelectorAll('textarea[name="note"]').forEach(strip);
  // If the server-rendered note already carries a block, verify it against the
  // live cart once per page load — otherwise a stale block (fees healed away
  // in an earlier session) would be cached and re-injected at checkout.
  const verifyOnLoad = () => {
    stripAll();
    if (!isCartPage() && gwNoteBlockCache) fetchCart().then(syncOrderNote).catch(() => {});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', verifyOnLoad);
  else verifyOnLoad();
  document.addEventListener('cart:refresh', () => setTimeout(stripAll, 600));
  let noteTimer;
  document.addEventListener(
    'change',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLTextAreaElement) || el.name !== 'note') return;
      el.value = gwWithBlock(el.value);
      setTimeout(() => strip(el), 0); // re-stripped before the next repaint
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => syncOrderNote().catch(() => {}), 900);
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      for (const el of event.target.querySelectorAll('textarea[name="note"]')) {
        el.value = gwWithBlock(el.value);
        setTimeout(() => strip(el), 100); // restore display if navigation is blocked
      }
    },
    true
  );
})();

/* ------------------------------- reconciler -------------------------------- */
/*
  Heals drift caused by edits made outside the dialog (item removed from a
  parcel, fee line removed or its quantity changed by hand):
  * fee line whose parcel has no members  -> removed
  * fee line with quantity != 1           -> reset to 1
  * members labelled wrapped with no fee  -> downgraded to a gift note parcel
  * orphaned "Gift note (Parcel N)" attrs -> removed
*/
let reconcileBusy = false;

function computeReconcileOps(cart) {
  const ops = [];
  const memberPids = new Set();

  for (const item of cart.items) {
    const props = item.properties || {};
    if (!(GW_FEE_KEY in props) && props[GW_HIDDEN_KEY]) memberPids.add(String(props[GW_HIDDEN_KEY]));
  }

  const feePids = new Set();
  for (const item of cart.items) {
    const props = item.properties || {};
    if (!(GW_FEE_KEY in props)) continue;
    const pid = String(props[GW_FEE_KEY]);
    if (!memberPids.has(pid)) {
      ops.push({ run: () => changeLine(item.key, 0) });
    } else {
      feePids.add(pid);
      if (item.quantity !== 1) ops.push({ run: () => changeLine(item.key, 1) });
    }
  }

  for (const item of cart.items) {
    const props = item.properties || {};
    if (GW_FEE_KEY in props || !props[GW_HIDDEN_KEY]) continue;
    const pid = String(props[GW_HIDDEN_KEY]);
    if (props[GW_WRAP_LABEL] && !feePids.has(pid)) {
      const downgraded = { ...stripGw(props), [GW_HIDDEN_KEY]: pid, [GW_NOTE_LABEL]: `Parcel ${pid}` };
      ops.push({ run: () => changeLine(item.key, item.quantity, downgraded) });
    }
  }

  const attributes = {};
  for (const [key, value] of Object.entries(cart.attributes || {})) {
    const match = key.match(GW_ATTR_RE);
    if (match && value && !memberPids.has(String(match[1]))) attributes[key] = null;
  }
  if (Object.keys(attributes).length > 0) ops.push({ run: () => updateAttributes(attributes) });

  return ops;
}

async function reconcile(cart, { silent } = {}) {
  if (reconcileBusy) return false;
  const ops = computeReconcileOps(cart);
  if (ops.length === 0) {
    // Nothing structural to heal — but the note block can still be stale
    // (order #408433: a bundle app rewrote the item lines, an earlier
    // reconcile removed the orphaned fees, and the "— GIFT WRAPPING —"
    // block survived into the packing list, so 9 parcels were wrapped
    // unpaid). syncOrderNote no-ops when the note already matches.
    syncOrderNote(cart).catch(() => {});
    return false;
  }

  reconcileBusy = true;
  try {
    for (const op of ops) await op.run();
    // Healing fees/attributes changes what the note block should say —
    // rebuild it from the healed cart or the packing list keeps the old story.
    await syncOrderNoteRetry();
  } catch (error) {
    console.warn('[gift-wrap] reconcile failed:', error);
  } finally {
    reconcileBusy = false;
  }
  if (!silent) refreshCartUI();
  return true;
}

document.addEventListener('cart:change', (event) => {
  const cart = event.detail && event.detail.cart;
  if (!cart || isCartPage()) return;
  reconcile(cart);
});

if (isCartPage()) {
  // The theme reloads the cart page after each change, so heal on load (guarded against reload loops).
  (async () => {
    const cart = await fetchCart().catch(() => null);
    if (!cart) return;
    if (computeReconcileOps(cart).length > 0 && !sessionStorage.getItem('gw-reconciled')) {
      sessionStorage.setItem('gw-reconciled', '1');
      const changed = await reconcile(cart, { silent: true });
      if (changed) window.location.reload();
    } else {
      sessionStorage.removeItem('gw-reconciled');
      // note-only staleness needs no reload — heal it quietly
      syncOrderNote(cart).catch(() => {});
    }
  })();
}

/* ------------------------------ line control ------------------------------- */

class GiftWrapLineControl extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => {
      const lineKey = this.querySelector('button').getAttribute('data-gw-line-key');
      document.dispatchEvent(new CustomEvent('gift-wrap:quick', { detail: { lineKey } }));
    });
  }
}

if (!window.customElements.get('gift-wrap-line-control')) {
  window.customElements.define('gift-wrap-line-control', GiftWrapLineControl);
}

/* -------------------------------- manager ---------------------------------- */

class GiftWrapManager extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.dialog = this.closest('x-modal');
    this.feeVariantId = Number(this.dataset.feeVariantId);
    try { this.styleVariants = JSON.parse(this.dataset.styleVariants || '{}'); } catch (e) { this.styleVariants = {}; }
    this.feePriceCents = Number(this.dataset.feePriceCents) || 0;
    this.feePriceLabel = this.dataset.feePriceLabel || formatMoney(this.feePriceCents);
    this.snapshot = this.innerHTML;
    this.saved = false;
    this.pendingQuickKey = null;

    this.addEventListener('change', this.onChange.bind(this), { signal });
    this.addEventListener('input', this.onInput.bind(this), { signal });
    this.addEventListener('click', this.onClick.bind(this), { signal });

    document.addEventListener('gift-wrap:quick', (event) => {
      this.pendingQuickKey = event.detail.lineKey;
    }, { signal });

    this.dialog?.addEventListener('dialog:after-show', this.onDialogShow.bind(this), { signal });
    this.dialog?.addEventListener('dialog:after-hide', this.onDialogHide.bind(this), { signal });

    this.refreshUI();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /* ------------ DOM accessors ------------ */

  itemRows() {
    return [...this.querySelectorAll('[data-gw-item]')];
  }

  parcelFieldsets() {
    return [...this.querySelectorAll('[data-gw-parcel]')].filter((el) => el.dataset.gwParcel !== '__PID__');
  }

  parcelState() {
    const parcels = {};
    for (const fieldset of this.parcelFieldsets()) {
      const pid = fieldset.dataset.gwParcel;
      parcels[pid] = {
        fieldset,
        wrap: fieldset.querySelector('[data-gw-wrap]').checked,
        style: fieldset.querySelector('.gw-style__input:checked')?.value || '',
        message: fieldset.querySelector('[data-gw-note]').value,
        members: []
      };
    }
    for (const row of this.itemRows()) {
      const pid = row.querySelector('[data-gw-parcel-select]').value;
      if (pid && parcels[pid]) parcels[pid].members.push(row);
    }
    return parcels;
  }

  /* ------------ event handling ------------ */

  onChange(event) {
    const together = event.target.closest('[data-gw-together]');
    if (together) {
      // Customer-facing mode switch. On: consolidate every gifted item into the
      // lowest parcel and hide the parcel machinery. Off: reveal parcel grouping.
      this.toggleAttribute('data-simple', together.checked);
      if (together.checked) {
        const pids = this.parcelFieldsets().map((el) => el.dataset.gwParcel).sort((a, b) => Number(a) - Number(b));
        const target = pids[0];
        if (target) {
          for (const row of this.itemRows()) {
            const sel = row.querySelector('[data-gw-parcel-select]');
            if (sel.value && sel.value !== target) {
              sel.value = target;
              this.guardExcludedItem(sel);
            }
          }
        }
      }
      this.refreshUI();
      return;
    }

    const simpleCheck = event.target.closest('[data-gw-simple-check]');
    if (simpleCheck) {
      // Simple mode: the checkbox proxies the (hidden) parcel selects — everything
      // gifts into the lowest existing parcel, created on first tick. It drives
      // ALL unit rows of its line, so a qty-5 line gifts as a whole. All parcel
      // logic (fees, guards, reconciliation) then runs unchanged off the selects.
      const row = simpleCheck.closest('[data-gw-item]');
      let target = '';
      if (simpleCheck.checked) {
        const pids = this.parcelFieldsets().map((el) => el.dataset.gwParcel).sort((a, b) => Number(a) - Number(b));
        target = pids[0];
        if (!target) {
          target = this.createParcel();
          this.syncParcelOptions();
        }
      }
      for (const unitRow of this.itemRows().filter((el) => el.dataset.lineKey === row.dataset.lineKey)) {
        const select = unitRow.querySelector('[data-gw-parcel-select]');
        select.value = target;
        this.guardExcludedItem(select);
      }
      this.refreshUI();
      return;
    }

    const select = event.target.closest('[data-gw-parcel-select]');
    if (select) {
      if (select.value === '__new__') {
        const pid = this.createParcel();
        this.syncParcelOptions();
        select.value = pid;
      }
      this.guardExcludedItem(select);
      this.refreshUI();
      return;
    }

    if (event.target.closest('[data-gw-wrap]')) {
      this.guardExcludedParcel(event.target.closest('[data-gw-parcel]'));
      this.refreshUI();
      return;
    }

    if (event.target.closest('[data-gw-qty]')) {
      const input = event.target.closest('[data-gw-qty]');
      const max = Number(input.max) || 1;
      input.value = Math.min(Math.max(Number(input.value) || 1, 1), max);
      this.refreshUI();
    }
  }

  onInput(event) {
    const note = event.target.closest('[data-gw-note]');
    if (note) {
      const fieldset = note.closest('[data-gw-parcel]');
      const counter = fieldset.querySelector('[data-gw-count]');
      if (counter) counter.textContent = `${note.value.length} / ${note.maxLength}`;
    }
  }

  onClick(event) {
    if (event.target.closest('[data-gw-save]')) this.save();
  }

  onDialogShow() {
    if (!this.pendingQuickKey) return;
    const rows = this.itemRows().filter((el) => el.dataset.lineKey === this.pendingQuickKey);
    this.pendingQuickKey = null;
    if (rows.length === 0) return;

    // "Gift this item" gifts the whole line: every unit row that is not yet
    // in a parcel joins the lowest one (created if none exists).
    let target = null;
    let changed = false;
    for (const row of rows) {
      const select = row.querySelector('[data-gw-parcel-select]');
      if (select.value !== '') continue;
      if (!target) {
        target = this.parcelFieldsets().map((el) => el.dataset.gwParcel).sort((a, b) => Number(a) - Number(b))[0];
        if (!target) {
          target = this.createParcel();
          this.syncParcelOptions();
        }
      }
      select.value = target;
      this.guardExcludedItem(select);
      changed = true;
    }
    if (changed) this.refreshUI();
    rows[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  onDialogHide() {
    if (!this.saved) {
      this.innerHTML = this.snapshot;
      // data-simple lives on this element, not in the restored innerHTML —
      // resync it from the restored "wrap together" checkbox state
      const together = this.querySelector('[data-gw-together]');
      if (together) this.toggleAttribute('data-simple', together.checked);
      this.refreshUI();
    }
    this.saved = false;
  }

  /* ------------ parcel management ------------ */

  createParcel() {
    const existing = this.parcelFieldsets().map((el) => Number(el.dataset.gwParcel)).filter((n) => !Number.isNaN(n));
    const pid = String(existing.length > 0 ? Math.max(...existing) + 1 : 1);
    const template = this.querySelector('template[data-gw-parcel-template]');
    const html = template.innerHTML.replaceAll('__PID__', pid);
    this.querySelector('[data-gw-parcels]').insertAdjacentHTML('beforeend', html);
    return pid;
  }

  syncParcelOptions() {
    const pids = this.parcelFieldsets().map((el) => el.dataset.gwParcel).sort((a, b) => Number(a) - Number(b));
    for (const row of this.itemRows()) {
      const select = row.querySelector('[data-gw-parcel-select]');
      const current = select.value;
      const options = [`<option value="">${gwT('not_a_gift', 'Not a gift')}</option>`]
        .concat(pids.map((pid) => `<option value="${pid}">${gwT('parcel', 'Parcel [n]').replace('[n]', pid)}</option>`))
        .concat([`<option value="__new__">${gwT('new_parcel', '+ New parcel')}</option>`]);
      select.innerHTML = options.join('');
      select.value = pids.includes(current) ? current : '';
    }
  }

  guardExcludedItem(select) {
    const row = select.closest('[data-gw-item]');
    if (!row || !row.hasAttribute('data-excluded') || !select.value) return;
    const fieldset = this.parcelFieldsets().find((el) => el.dataset.gwParcel === select.value);
    // unit rows carry no hint element of their own — fall back to the line's first row
    const hint = row.querySelector('[data-gw-item-too-large]')
      || this.itemRows().find((el) => el.dataset.lineKey === row.dataset.lineKey)?.querySelector('[data-gw-item-too-large]');
    if (fieldset && fieldset.querySelector('[data-gw-wrap]').checked) {
      select.value = '';
      if (hint) hint.hidden = false;
    } else if (hint) {
      hint.hidden = true;
    }
  }

  guardExcludedParcel(fieldset) {
    const wrap = fieldset.querySelector('[data-gw-wrap]');
    const warning = fieldset.querySelector('[data-gw-too-large]');
    if (!wrap.checked) {
      if (warning) warning.hidden = true;
      return;
    }
    const pid = fieldset.dataset.gwParcel;
    const hasExcludedMember = this.itemRows().some(
      (row) => row.hasAttribute('data-excluded') && row.querySelector('[data-gw-parcel-select]').value === pid
    );
    if (hasExcludedMember) {
      wrap.checked = false;
      if (warning) warning.hidden = false;
    } else if (warning) {
      warning.hidden = true;
    }
  }

  /* ------------ rendering ------------ */

  renumberUnits() {
    // Apps/cart transforms can split one product's quantity across several
    // lines, so its unit rows would otherwise restart at #1 per card —
    // number them continuously per product instead.
    const counters = new Map();
    for (const row of this.itemRows()) {
      const label = row.querySelector('.gw-item__unit');
      if (!label) continue;
      const groupKey = row.dataset.variantId + '|' + (row.dataset.gwTitle || '');
      const n = (counters.get(groupKey) || 0) + 1;
      counters.set(groupKey, n);
      label.textContent = '#' + n;
    }
  }

  refreshUI() {
    this.renumberUnits();
    const parcels = this.parcelState();

    for (const [pid, parcel] of Object.entries(parcels)) {
      const membersEl = parcel.fieldset.querySelector('[data-gw-members]');
      if (membersEl) {
        if (parcel.members.length === 0) {
          membersEl.textContent = gwT('members_empty', 'No items in this parcel yet.');
        } else {
          // aggregate unit rows of the same product: "Title × 2"
          const counts = new Map();
          for (const row of parcel.members) {
            const title = row.dataset.gwTitle
              || row.querySelector('.gw-item__title')?.textContent.trim().replace(/\s+/g, ' ')
              || 'Item';
            const qtyInput = row.querySelector('[data-gw-qty]');
            const qty = qtyInput && !qtyInput.closest('[data-gw-qty-wrap]').hidden
              ? Number(qtyInput.value) || 1
              : Number(row.dataset.qty) || 1;
            counts.set(title, (counts.get(title) || 0) + qty);
          }
          const names = [...counts.entries()].map(([title, qty]) => (qty > 1 ? `${title} × ${qty}` : title));
          membersEl.textContent = gwT('contains', 'Contains: [items]').replace('[items]', names.join(', '));
        }
      }

      parcel.fieldset.toggleAttribute('data-gw-empty', parcel.members.length === 0);
      parcel.fieldset.querySelector('[data-gw-styles]').hidden = !parcel.wrap;

      const counter = parcel.fieldset.querySelector('[data-gw-count]');
      const note = parcel.fieldset.querySelector('[data-gw-note]');
      if (counter && note) counter.textContent = `${note.value.length} / ${note.maxLength}`;
    }

    for (const row of this.itemRows()) {
      const select = row.querySelector('[data-gw-parcel-select]');
      const qtyWrap = row.querySelector('[data-gw-qty-wrap]');
      if (qtyWrap) qtyWrap.hidden = select.value === '';

      const simpleCheck = row.querySelector('[data-gw-simple-check]');
      if (simpleCheck) simpleCheck.checked = select.value !== '';

      if (row.hasAttribute('data-excluded')) {
        for (const option of select.options) {
          if (!option.value || option.value === '__new__') continue;
          const parcel = parcels[option.value];
          option.disabled = Boolean(parcel && parcel.wrap);
        }
      }
    }

    const wrappedCount = Object.values(parcels).filter((parcel) => parcel.wrap && parcel.members.length > 0).length;
    const total = this.querySelector('[data-gw-total]');
    if (total) {
      total.hidden = wrappedCount === 0;
      const totalTemplate = wrappedCount === 1
        ? gwT('total_one', 'Gift wrap: 1 parcel × [price] = [total]')
        : gwT('total_other', 'Gift wrap: [n] parcels × [price] = [total]');
      total.textContent = totalTemplate
        .replace('[n]', wrappedCount)
        .replace('[price]', this.feePriceLabel)
        .replace('[total]', formatMoney(wrappedCount * this.feePriceCents));
    }
  }

  /* ------------ save ------------ */

  collectDesiredState() {
    const parcels = this.parcelState();
    const desired = { items: [], parcels: {} };
    const byKey = new Map();

    for (const row of this.itemRows()) {
      const select = row.querySelector('[data-gw-parcel-select]');
      const parcelId = parcels[select.value] ? select.value : '';
      const rowQty = Number(row.dataset.qty) || 1;

      let entry = byKey.get(row.dataset.lineKey);
      if (!entry) {
        entry = { key: row.dataset.lineKey, variantId: Number(row.dataset.variantId), allocations: [] };
        byKey.set(entry.key, entry);
        desired.items.push(entry);
      }

      const qtyInput = row.querySelector('[data-gw-qty]');
      if (qtyInput && parcelId) {
        // capped fallback row (qty above the per-unit limit): gifted portion + plain remainder
        const gifted = Math.min(Math.max(Number(qtyInput.value) || 1, 1), rowQty);
        entry.allocations.push({ parcelId, qty: gifted });
        if (rowQty - gifted > 0) entry.allocations.push({ parcelId: '', qty: rowQty - gifted });
      } else {
        entry.allocations.push({ parcelId, qty: rowQty });
      }
    }

    for (const [pid, parcel] of Object.entries(parcels)) {
      if (parcel.members.length === 0) continue;
      desired.parcels[pid] = {
        wrap: parcel.wrap,
        style: parcel.style,
        message: parcel.message
      };
    }

    return desired;
  }

  async save() {
    const button = this.querySelector('[data-gw-save]');
    const errorEl = this.querySelector('[data-gw-error]');
    if (button?.getAttribute('aria-busy') === 'true') return;

    button?.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;
    loading(true);

    try {
      await applyGiftState(this.collectDesiredState(), this.feeVariantId, this.styleVariants);
      this.saved = true;
      await this.dialog?.hide();
      refreshCartUI();
    } catch (error) {
      console.warn('[gift-wrap] save failed:', error);
      if (errorEl) {
        errorEl.textContent = error.message || gwT('error', 'Something went wrong updating your gift options. Please try again.');
        errorEl.hidden = false;
      }
      // Reflect whatever partially applied so the customer never sees a stale cart.
      if (!isCartPage()) document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      loading(false);
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('gift-wrap-manager')) {
  window.customElements.define('gift-wrap-manager', GiftWrapManager);
}
